import { RegionShared } from "@1games-ru/region-shared";
import {
  NotificationGameType,
  NotificationSystemType,
  NotificationTransactionType,
} from "@1games-ru/region-shared/dist/enums/notification/notification-cef";
import { useCallback, useState } from "react";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import {
  ExtendedNotificationType,
  useNotificationStore,
} from "@/stores/notifications/notificationStore";
import { NotificationsActionType } from "@/domains/notifications/constants/notificationsActionType";
import toast from "@/domains/toasts/helpers/toast";

export default function useFridgeHandlers() {
  const [isGettingFridge, setIsGettingFridge] = useState(false);
  const [isRestocking, setIsRestocking] = useState(false);
  const [isCrafting, setIsCrafting] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isCreateOrderDelivery, setIsCreateOrderDelivery] = useState(false);
  const { addNotification } = useNotificationStore();

  const resetFlags = useCallback(() => {
    setIsGettingFridge(false);
    setIsRestocking(false);
    setIsCrafting(false);
    setIsUpgrading(false);
    setIsCreateOrderDelivery(false);
  }, []);

  const getFridge = useCallback(
    async (data: typeof RegionShared.Backend.Requests.FridgeGetFridgeRequest) => {
      setIsGettingFridge(true);
      const response = await EstateMpProvider.getFridge(data);

      setIsGettingFridge(false);
      if (response instanceof RpcMpProviderSuccessResponse) {
        return response.data;
      }

      toast.error(response.message);
      return null;
    },
    [],
  );

  const restock = useCallback(
    async (data: typeof RegionShared.Backend.Requests.FridgeRestockRequest) => {
      setIsRestocking(true);
      const response = await EstateMpProvider.restock(data);
      setIsRestocking(false);
      if (response instanceof RpcMpProviderSuccessResponse) {
        addNotification({
          type: ExtendedNotificationType.Action,
          data: { type: NotificationsActionType.FridgeProductsRestocked },
        });
        return true;
      }

      addNotification({
        type: ExtendedNotificationType.System,
        data: { type: NotificationSystemType.MissingItem, payload: { item: response.message } },
      });
      return false;
    },
    [],
  );

  const craftItem = useCallback(
    async (data: typeof RegionShared.Backend.Requests.FridgeCraftItemRequest) => {
      setIsCrafting(true);

      const response = await EstateMpProvider.craftItem(data);
      setIsCrafting(false);
      if (response instanceof RpcMpProviderFailedResponse) {
        addNotification({
          type: ExtendedNotificationType.Action,
          data: { type: NotificationsActionType.FridgeNotEnoughtProducts },
        });
        return false;
      }

      return true;
    },
    [],
  );

  const upgrade = useCallback(
    async (data: typeof RegionShared.Backend.Requests.FridgeBuyUpgradeRequest) => {
      setIsUpgrading(true);

      const response = await EstateMpProvider.upgradeFridge(data);

      setIsUpgrading(false);
      if (response instanceof RpcMpProviderSuccessResponse) {
        return true;
      }

      toast.error(response.message);
      return false;
    },
    [],
  );

  const createOrderDelivery = useCallback(
    async (data: typeof RegionShared.Backend.Requests.FridgeCreateDeliveryRequest) => {
      setIsCreateOrderDelivery(true);

      const response = await EstateMpProvider.createOrderDelivery(data);

      setIsCreateOrderDelivery(false);
      if (response instanceof RpcMpProviderSuccessResponse) {
        addNotification({
          type: ExtendedNotificationType.Game,
          data: {
            type: NotificationGameType.FridgeSuccessfulDelivered,
          },
        });
        addNotification({
          type: ExtendedNotificationType.Transaction,
          data: {
            type: NotificationTransactionType.Decrement,
            paymentType: data.paymentMethod,
            amount: data.restockAmount,
          },
        });
        return true;
      }

      addNotification({
        type: ExtendedNotificationType.Action,
        data: { type: NotificationsActionType.NotEnoughMoney },
      });
      return false;
    },
    [],
  );

  return {
    getFridge,
    isGettingFridge,
    restock,
    craftItem,
    upgrade,
    isRestocking,
    isUpgrading,
    isCrafting,
    createOrderDelivery,
    isCreateOrderDelivery,
    resetFlags,
  };
}
